import {
  getAuthorizationHeader,
  getHeadersWithUniqueVisitorId,
  ProductRewardSearchResult,
} from "@brandclub/common-ui";
import { HOST_DOMAIN } from "../../utils/domain";
import { DtcSiteProduct, DtcSiteProductResponse } from "../types";
import { getCommonRequestHeaders } from "../../utils/axios";

const DOMAIN = HOST_DOMAIN + "/api-rewards";

export interface BrandclubProductByRetailerSkuResponse {
  stacklineSku: string;
  catalogSku?: string;
  title: string;
  retailerId: number;
  retailerSku: string;
  retailerParentSku?: string;
  channelType?: string;
  imageUrl?: string;
  retailerProductPageUrl?: string;
}

export const autoUpdateDTCProducts = async (
  dtcProducts: {
    stacklineSku: string;
    dtcProductPageUrl: string;
    dtcSku: string | undefined;
  }[],
  signal?: AbortSignal
) => {
  const headers = {
    ...(await getHeadersWithUniqueVisitorId()),
    ...getCommonRequestHeaders(),
  };
  const res = await fetch(DOMAIN + `/product/autoUpdateDTCProducts`, {
    method: "POST",
    headers,
    signal,

    body: JSON.stringify({ dtcProducts }),
  });
  if (res.ok) {
    return await res.json();
  } else {
    throw new Error("Error auto updating DTC products");
  }
};

export async function setToRecentlyViewedProduct(
  sku: string,
  eventName: string,
  paramsToTrack: Record<string, any>
) {
  const data = { ...paramsToTrack, eventName };
  const url = DOMAIN + `/recentlyViewed/setProduct?sku=${sku}`;

  const res = await fetch(url, {
    method: "POST",
    headers: {
      ...(await getAuthorizationHeader()),
      ...getCommonRequestHeaders(),
    },
    body: JSON.stringify(data),
  });
  if (res.ok) {
    return await res.json();
  } else {
    throw new Error("Error setting recently viewed product");
  }
}

export const getBrandclubProduct = async (
  stacklineSku: string,
  signal?: AbortSignal
): Promise<ProductRewardSearchResult> => {
  const res = await fetch(DOMAIN + `/product/${stacklineSku}`, {
    headers: {
      ...(await getAuthorizationHeader()),
      ...getCommonRequestHeaders(),
    },
    signal,
  });
  if (res.ok) {
    return res.json();
  } else {
    throw new Error("Error fetching brandclub product");
  }
};

export const getBrandclubProductByRetailerSku = async (
  retailerSku: string,
  storeKind?: string,
  signal?: AbortSignal
): Promise<BrandclubProductByRetailerSkuResponse> => {
  if (!storeKind) {
    throw new Error("Error fetching brandclub product by retailer SKU: missing storeKind");
  }
  const encodedStore = encodeURIComponent(storeKind);
  const encodedSku = encodeURIComponent(retailerSku);
  const res = await fetch(
    DOMAIN + `/catalog-v2/stores/${encodedStore}/products/${encodedSku}`,
    {
      headers: {
        ...(await getAuthorizationHeader()),
        ...getCommonRequestHeaders(),
      },
      signal,
    }
  );
  if (res.ok) {
    return res.json();
  } else {
    throw new Error("Error fetching brandclub product by retailer SKU");
  }
};

const validateDTCSiteProductResponse = (
  data: unknown
): DtcSiteProductResponse => {
  if (typeof data !== "object") {
    throw new Error(
      `Received unexpected dtc product data type: ${typeof data}`
    );
  }

  if (data && "product" in data) {
    return data as DtcSiteProductResponse;
  }

  // no product found
  throw new Error("No product found");
};

/**
 * Fetches the Shopify product data for the given product URL
 * @param dtcSiteProductUrl The URL of the product
 * @param signal The signal to abort the request
 * @returns The shopify product data
 */
export const fetchDtcSiteProduct = async (
  dtcSiteProductUrl: string,
  signal?: AbortSignal
): Promise<DtcSiteProduct | null> => {
  let url = dtcSiteProductUrl;
  if (url && url.endsWith("/")) {
    url = url.slice(0, -1);
  }

  // If the URl matches the origin, we should not fetch the product.json
  if (url === window.location.origin) {
    console.warn("Cannot fetch product.json from the same origin");
    return null;
  }

  const response = await fetch(url + "/product.json", {
    method: "GET",
    signal,
  });
  if (!response.ok) {
    throw new Error("Failed to fetch site product");
  }
  const data = await response.json();
  return validateDTCSiteProductResponse(data).product;
};
